import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { FolderOpen, Plus, Search, Trash2, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CreateCollectionDialog } from "@/components/collections/CreateCollectionDialog";
import { SyncStatusBadge } from "@/components/collections/SyncStatusBadge";
import type { CollectionSyncStatus } from "@/types/domain";
import { toast } from "sonner";

export function CollectionsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const utils = trpc.useUtils();
  const { data: collections, isLoading } = trpc.collections.list.useQuery({});
  const { data: prodConfigured } = trpc.collections.isProdConfigured.useQuery();

  const filteredCollections = useMemo(() => {
    if (!collections) return collections;
    const query = search.trim().toLowerCase();
    if (!query) return collections;
    return collections.filter((col: any) => {
      const name = (col.name ?? "").toLowerCase();
      const description = (col.description ?? "").toLowerCase();
      return name.includes(query) || description.includes(query);
    });
  }, [collections, search]);

  const deleteMutation = trpc.collections.delete.useMutation({
    onSuccess: () => {
      utils.collections.list.invalidate();
      toast.success("Collection deleted");
      setDeleteId(null);
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Collections</h1>
          <p className="text-muted-foreground mt-1">
            Curate groups of locations for export and sharing
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Collection
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !collections?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderOpen className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No collections yet</h3>
            <p className="text-muted-foreground text-sm mt-1 mb-4">
              Create a collection to group locations for export
            </p>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Collection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search collections"
              className="pl-8 h-9"
            />
          </div>
          {!filteredCollections?.length ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No collections match &ldquo;{search}&rdquo;.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredCollections.map((col: any) => (
            <Link
              key={col.id}
              to={`/dashboard/collections/${col.id}` as string}
              className="block"
            >
              <Card className="group hover:border-foreground/20 transition-colors cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="h-4 w-4 rounded-full shrink-0"
                        style={{ backgroundColor: col.color }}
                      />
                      <div className="min-w-0">
                        <h3 className="font-medium truncate">{col.name}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {col.locationCount} location
                          {col.locationCount !== 1 ? "s" : ""}
                        </p>
                        {prodConfigured?.configured && col.syncStatus && (
                          <SyncStatusBadge
                            status={col.syncStatus as CollectionSyncStatus}
                            pushed={col.syncPushed}
                            total={col.syncTotal}
                          />
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive shrink-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteId(col.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {col.description && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {col.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-3">
                    Updated{" "}
                    {new Date(col.updated_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            </Link>
              ))}
            </div>
          )}
        </>
      )}

      <CreateCollectionDialog
        open={showCreate}
        onOpenChange={setShowCreate}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete collection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the collection grouping. The locations themselves
              will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteId && deleteMutation.mutate({ id: deleteId })
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
